<template>
  <div class="cheque-section w-100">
    <!-- Header Section -->
    <div class="cheque-header">
      <a-divider class="cheque-divider">
        <span class="divider-text">{{ $t("recette.saisirInfoCheque") }}</span>
      </a-divider>
    </div>

    <!-- Column Headers -->
    <div class="cheque-columns-header">
      <div class="column-header index-col">Indice</div>
      <div class="column-header">Numéro</div>
      <div class="column-header">Date d'échéance</div>
      <div class="column-header">Banque</div>
      <div class="column-header">Titulaire</div>
      <div class="column-header">Montant</div>
      <div class="column-header">Validité</div>
      <div class="column-header actions-col">Actions</div>
    </div>

    <!-- Cheque Items -->
    <div class="cheque-items">
      <div
        v-for="(item, index) in chequesData"
        :key="index"
        class="cheque-item"
        :class="{ 'cheque-item--highlighted': index % 2 === 0 }"
      >
        <div class="cheque-index">
          <span class="cheque-number">{{ index + 1 }}</span>
        </div>

        <div class="cheque-field">
          <a-input
            :value="item.num"
            placeholder="Numéro du chèque"
            size="small"
            :class="{ 'input-error': isFieldInvalid(index, 'num') }"
            @input="(e) => handleNumInput(index, e.target.value)"
            @change="(e) => handleChequeUpdate(index, 'num', e.target.value)"
            @blur="(e) => verifyChequeExists(index)"
          />
        </div>

        <div class="cheque-field">
          <a-date-picker
            :value="item.dateEchance"
            placeholder="Date"
            size="small"
            style="width: 100%"
            :class="{ 'input-error': isFieldInvalid(index, 'dateEchance') }"
            @change="(date) => handleChequeUpdate(index, 'dateEchance', date)"
          />
        </div>

        <div class="cheque-field">
          <BankSelect
            :value="item.bank"
            placeholder="Nom de la banque"
            size="small"
            :class="{ 'input-error': isFieldInvalid(index, 'bank') }"
            @change="(value) => handleChequeUpdate(index, 'bank', value)"
          />
        </div>

        <div class="cheque-field">
          <a-input
            :value="item.titulaire"
            placeholder="Nom du titulaire"
            size="small"
            :class="{ 'input-error': isFieldInvalid(index, 'titulaire') }"
            @change="
              (e) => handleChequeUpdate(index, 'titulaire', e.target.value)
            "
            @blur="(e) => verifyChequeExists(index)"
          />
        </div>

        <div class="cheque-field">
          <a-input-number
            :value="item.montant"
            placeholder="Montant"
            size="small"
            style="width: 100%"
            :formatter="
              (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            "
            :parser="(value) => value.replace(/\$\s?|(,*)/g, '')"
            :class="{ 'input-error': isFieldInvalid(index, 'montant') }"
            @change="(value) => handleChequeUpdate(index, 'montant', value)"
          />
        </div>

        <div class="cheque-field validity-field">
          <div :class="getValidityClass(index)">
            <a-spin v-if="isVerifying(index)" size="small" />
            <a-icon v-else :type="getValidityIcon(index)" />
          </div>
        </div>

        <div class="cheque-actions">
          <a-button
            v-if="index > 0"
            type="text"
            size="small"
            danger
            @click="handleRemoveCheque(index)"
            class="remove-btn"
            icon="delete"
            title="Supprimer ce chèque"
          >
          </a-button>
        </div>
      </div>
    </div>

    <!-- Add Cheque Button -->
    <div class="cheque-actions-footer">
      <a-button
        type="dashed"
        icon="plus"
        @click="handleAddCheque"
        class="add-cheque-btn"
        size="large"
      >
        Ajouter un chèque
      </a-button>
    </div>

    <!-- Cheque Details Modal -->
    <ChequeVerifyModal
      :visible="chequeDetailsModal"
      :cheque="selectedCheque"
      @close="closeChequeModal"
    />
  </div>
</template>

<script>
import BankSelect from "@/components/BankSelect.vue";
import ChequeVerifyModal from "@/components/comptabilite/chequeVerifyModal.vue";
import apiClient from "@/services/axios";

export default {
  name: "ChequeSection",

  components: {
    BankSelect,
    ChequeVerifyModal,
  },

  props: {
    chequesData: {
      required: true,
      type: Array,
    },
    chequeVide: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      requiredFields: ["num", "dateEchance", "bank", "titulaire", "montant"],
      validationErrors: [],
      chequeDetailsModal: false,
      selectedCheque: null,
      existingCheques: {}, // Track which cheques exist in DB
      verifyingCheques: {}, // Track which specific cheques are being verified
    };
  },

  methods: {
    handleChequeUpdate(index, field, value) {
      this.$emit("update-cheque", index, field, value);
      if (field === "montant") {
        this.$emit("calculate-montant");
      }
      // Clear validity status when key fields change
      if (["num", "bank", "titulaire"].includes(field)) {
        this.$set(this.existingCheques, index, false);
        this.$set(this.verifyingCheques, index, false);
      }
      if (field === "bank") {
        this.verifyChequeExists(index);
      }
      this.validateCheque(index);
    },

    handleAddCheque() {
      // Validate last cheque before adding
      const lastIndex = this.chequesData.length - 1;
      if (!this.validateCheque(lastIndex)) {
        return;
      }
      this.$emit("add-cheque");
    },

    handleRemoveCheque(index) {
      this.$emit("remove-cheque", index);
      this.validationErrors.splice(index, 1);
      // Clean up validity tracking
      this.$delete(this.existingCheques, index);
      this.$delete(this.verifyingCheques, index);
    },

    validateCheque(index) {
      const cheque = this.chequesData[index];
      if (!cheque) return true;
      const errors = {};
      this.requiredFields.forEach((field) => {
        if (field === "montant") {
          if (!cheque[field] || cheque[field] <= 0) {
            errors[field] = true;
          }
        } else if (field === "num") {
          if (!cheque[field] || cheque[field].length === 0) {
            errors[field] = true;
          } else if (!/^[0-9]+$/.test(cheque[field])) {
            errors[field] = true;
          }
        } else {
          if (!cheque[field]) {
            errors[field] = true;
          }
        }
      });
      this.$set(this.validationErrors, index, errors);
      return Object.keys(errors).length === 0;
    },

    isFieldInvalid(index, field) {
      return (
        this.validationErrors[index] && this.validationErrors[index][field]
      );
    },

    handleNumInput(index, value) {
      const num = value.replace(/[^0-9]/g, "");
      this.$emit("update-cheque", index, "num", num);
    },

    async verifyChequeExists(index) {
      const cheque = this.chequesData[index];

      // Check if all required fields for verification are filled
      if (!cheque.num || !cheque.bank || !cheque.titulaire) {
        // Clear existing status if fields are empty
        this.$set(this.existingCheques, index, false);
        this.$set(this.verifyingCheques, index, false);
        return;
      }

      // Mark this specific cheque as being verified
      this.$set(this.verifyingCheques, index, true);

      try {
        // Make API request to check if cheque exists
        const response = await apiClient.get(`/compV3/cheque-traite/verify`, {
          params: {
            num: cheque.num,
            bank: cheque.bank,
            titulaire: cheque.titulaire,
          },
        });

        if (response.data.success && response.data.cheque) {
          // Cheque exists, show modal with details
          this.selectedCheque = response.data.cheque;
          this.chequeDetailsModal = true;
          // Mark this cheque as existing in DB (not valid)
          this.$set(this.existingCheques, index, true);
        } else {
          // Cheque doesn't exist (valid)
          this.$set(this.existingCheques, index, false);
        }
      } catch (error) {
        // Cheque doesn't exist or error occurred (assume valid)
        console.log(
          "Cheque verification:",
          error.response?.data?.message || "Cheque not found"
        );
        this.$set(this.existingCheques, index, false);
      } finally {
        // Mark this cheque as no longer being verified
        this.$set(this.verifyingCheques, index, false);
      }
    },

    isVerifying(index) {
      return this.verifyingCheques[index] === true;
    },

    getValidityIcon(index) {
      return this.existingCheques[index] ? "close" : "check";
    },

    getValidityClass(index) {
      return this.existingCheques[index] ? "invalid" : "valid";
    },

    closeChequeModal() {
      this.chequeDetailsModal = false;
      this.selectedCheque = null;
    },
  },
};
</script>

<style scoped>
.cheque-section {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.cheque-header {
  padding: 16px 24px 0;
}

.cheque-divider {
  margin: 0;
}

.divider-text {
  font-weight: 600;
  color: #1890ff;
  font-size: 16px;
}

.cheque-columns-header {
  display: grid;
  grid-template-columns: 60px 1fr 1fr 1fr 1fr 1fr 1fr 80px;
  gap: 12px;
  padding: 16px 24px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
  color: #262626;
  font-size: 14px;
}

.column-header {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.column-header:first-child,
.column-header:last-child {
  justify-content: center;
}

.cheque-items {
  max-height: 400px;
  overflow-y: auto;
}

.cheque-item {
  display: grid;
  grid-template-columns: 60px 1fr 1fr 1fr 1fr 1fr 1fr 80px;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s ease;
}

.cheque-item:hover {
  background: #f6ffed;
}

.cheque-item--highlighted {
  background: #fafafa;
}

.cheque-item--highlighted:hover {
  background: #f6ffed;
}

.cheque-index {
  display: flex;
  align-items: center;
  justify-content: center;
}

.cheque-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #1890ff;
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 14px;
}

.cheque-field {
  display: flex;
  align-items: center;
}

.cheque-actions {
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn {
  color: #ff4d4f;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.remove-btn:hover {
  background: #fff2f0;
  color: #ff7875;
}

.cheque-actions-footer {
  padding: 24px;
  text-align: center;
  border-top: 1px solid #f0f0f0;
}

.add-cheque-btn {
  height: 48px;
  padding: 0 32px;
  font-size: 16px;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.add-cheque-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.radio-option {
  margin-right: 16px;
}

.radio-label {
  font-size: 13px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .cheque-columns-header,
  .cheque-item {
    grid-template-columns: 50px 1fr 1fr 1fr 1fr 1fr 1fr 60px;
    gap: 8px;
    padding: 12px 16px;
  }

  .cheque-columns-header {
    padding: 12px 16px;
    font-size: 12px;
  }
}

@media (max-width: 768px) {
  .cheque-columns-header,
  .cheque-item {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 12px;
  }

  .column-header {
    justify-content: flex-start;
    text-align: left;
  }

  .cheque-field {
    flex-direction: column;
    align-items: flex-start;
  }

  .cheque-actions {
    justify-content: flex-start;
  }

  .cheque-index {
    justify-content: flex-start;
  }
}

/* Scrollbar Styling */
.cheque-items::-webkit-scrollbar {
  width: 6px;
}

.cheque-items::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.cheque-items::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.cheque-items::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.input-error {
  border: 1px solid #ff4d4f !important;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
}

.validity-field {
  display: flex;
  align-items: center;
  justify-content: center;
}

.valid {
  color: #52c41a;
}

.invalid {
  color: #ff4d4f;
}

.anticon {
  font-size: 14px;
}
</style>
